import React from "react";
import { Field, Input, Select } from "../../../components/ui/Field";
import { formatProductionDuration, getBatchTiming } from "./batchFormUtils";

const BatchFormFields = ({
  values, onChange, rawMaterialOptions = [], partOptions = [], errors = {}, disabled = false, loading = false,
  inputRecords = [], loadingInputs = false, existingBatch,
}) => {
  const { cycleTime, duration } = getBatchTiming(values, partOptions);
  const part = partOptions.find((option) => option.value === values.item_id);
  const assignmentsLocked = Boolean(existingBatch?.started_at || existingBatch?.cycles?.length);
  const availableInputs = inputRecords.filter((input) => !input.is_processed
    || input.input_ready_batch_id === existingBatch?.input_ready_batch_id);
  return (
    <fieldset disabled={disabled || loading} className="grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2">
      <Field label="Raw Material" required error={errors.raw_material} className="sm:col-span-2">
        <Select aria-label="Raw Material" value={values.raw_material || ""} disabled={assignmentsLocked}
          onChange={(event) => onChange("raw_material", event.target.value)} aria-invalid={Boolean(errors.raw_material)}>
          <option value="">{loading ? "Loading materials…" : "Select material…"}</option>
          {rawMaterialOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </Select>
        {!loading && !rawMaterialOptions.length && <p className="text-xs text-amber-600">Add a raw material for this plant before creating a batch.</p>}
      </Field>

      <Field label="Input Batch" required error={errors.input_ready_batch_id} className="sm:col-span-2">
        <Select aria-label="Input Batch" value={values.input_ready_batch_id || ""}
          disabled={!values.raw_material || loadingInputs || assignmentsLocked}
          onChange={(event) => onChange("input_ready_batch_id", event.target.value)} aria-invalid={Boolean(errors.input_ready_batch_id)}>
          <option value="">{assignmentsLocked && !values.input_ready_batch_id ? "Existing batch (no input record linked)"
            : !values.raw_material ? "Select a raw material first…" : loadingInputs ? "Loading input batches…" : "Select input batch…"}</option>
          {availableInputs.map((input) => <option key={input.input_ready_batch_id} value={input.input_ready_batch_id}>
            {input.input_ready_batch_id} · Quantity: {input.quantity ?? "—"} · {input.item_code || input.item_id || "No linked item"}
          </option>)}
        </Select>
        {values.raw_material && !loadingInputs && !availableInputs.length && !assignmentsLocked && <p className="text-xs text-amber-600">No available input batches for this raw material. Add an input batch with a linked item first.</p>}
      </Field>

      <Field label="Part to be Produced / Final FG" error={errors.item_id} hint="This item will be produced from the selected input batch.">
        <Input aria-label="Part to be Produced / Final FG" value={part?.label || values.item_id || ""} readOnly placeholder="—" className="bg-slate-50" />
      </Field>

      <Field label="Cycle Time (Autofilled)" hint={values.item_id && !(cycleTime > 0) ? "No cycle time is configured for this part." : "Populated from the selected part."}>
        <Input aria-label="Cycle Time (Autofilled)" value={cycleTime > 0 ? `${cycleTime}s` : ""} readOnly placeholder="—" className="bg-slate-50" />
      </Field>

      <Field label="Quantity" required error={errors.batch_quantity} hint="Populated from the selected input batch.">
        <Input aria-label="Quantity" type="number" min={1} step={1} value={values.batch_quantity ?? ""}
          readOnly placeholder="—" className="bg-slate-50" aria-invalid={Boolean(errors.batch_quantity)} />
      </Field>

      <Field label="Production Duration (Autofilled)" hint="Quantity × cycle time.">
        <Input aria-label="Production Duration (Autofilled)" value={formatProductionDuration(duration)} readOnly placeholder="—" className="bg-slate-50" />
      </Field>

      <Field label="Target Date" error={errors.target_delivery_at}>
        <Input aria-label="Target Date" type="date" value={values.target_delivery_at || ""}
          onChange={(event) => onChange("target_delivery_at", event.target.value)} />
      </Field>

      <Field label="RM SAP Cde" hint="Populated from the raw material code.">
        <Input aria-label="RM SAP Cde" value={values.sap_order_number || ""}
          readOnly placeholder="—" className="bg-slate-50" />
      </Field>

      <label className="flex items-center gap-2 text-sm text-slate-600 sm:col-span-2">
        <input type="checkbox" checked={Boolean(values.trial_mode)} onChange={(event) => onChange("trial_mode", event.target.checked)}
          className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand" />
        Trial Batch
      </label>
    </fieldset>
  );
};

export default BatchFormFields;
